import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { LoanRequest, LoanQuote } from './loans.types';
import { TaskStatus } from '../../../../shared/modules/legacy-tasks/tasks.types';

@Injectable({
  providedIn: 'root',
})
export class LoansService {
  // Private
  private _loanRequests: BehaviorSubject<LoanRequest[] | null>;
  private _loanRequest: BehaviorSubject<LoanRequest | null>;
  private _loanQuotes: BehaviorSubject<LoanQuote[] | null>;
  private _loanQuote: BehaviorSubject<LoanQuote | null>;
  // Observables that can be executable from outside
  selectedLoanRequestChange: BehaviorSubject<any>;

  private _projectId: string;
  private _accountId: string;

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _httpClient: HttpClient) {
    // Set the private defaults
    this._loanRequests = new BehaviorSubject(null);
    this._loanRequest = new BehaviorSubject(null);
    this._loanQuotes = new BehaviorSubject(null);
    this._loanQuote = new BehaviorSubject(null);

    // Set the defaults
    this.selectedLoanRequestChange = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for loanRequests
   */
  get loanRequests$(): Observable<LoanRequest[]> {
    return this._loanRequests.asObservable();
  }

  /**
   * Getter for contact
   */
  get loanRequest$(): Observable<LoanRequest> {
    return this._loanRequest.asObservable();
  }

  get loanQuotes$(): Observable<LoanQuote[]> {
    return this._loanQuotes.asObservable();
  }

  get loanQuote$(): Observable<LoanQuote> {
    return this._loanQuote.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set projectId(val: string) {
    this._projectId = val;
  }

  get projectId() {
    return this._projectId;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get contacts
   *
   * @param sortField
   * @param sortDirection
   */
  getLoanRequests(): Observable<LoanRequest[]> {
    return this._httpClient.get<any>(`projects/${this.projectId}/loan_requests`).pipe(
      tap((response: any) => {
        this._loanRequests.next(response.loan_requests);
      }),
    );
  }

  /**
   * Get project by id
   */
  getLoanRequestById(id: string): Observable<LoanRequest> {
    return this._httpClient.get<any>(`projects/${this.projectId}/loan_requests/${id}`).pipe(
      tap((response) => {
        this._loanRequest.next(response.loan_request);
        this._loanQuotes.next(response.loan_request.loan_quotes);
        return response.loan_request;
      }),
    );
  }

  createLoanRequest(loanRequest: LoanRequest): Observable<LoanRequest> {
    return this.loanRequests$.pipe(
      take(1),
      switchMap((loanRequests) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/loan_requests`, {
            loan_request: loanRequest,
          })
          .pipe(
            map((reponse) => {
              // Update the contacts with the new contact
              this._loanRequests.next([reponse.loan_request, ...loanRequests]);

              // Return the new contact
              return reponse.loan_request;
            }),
          ),
      ),
    );
  }

  updateLoanRequest(loanRequest: LoanRequest): Observable<LoanRequest> {
    return this.loanRequests$.pipe(
      take(1),
      switchMap((loanRequests) =>
        this._httpClient
          .put<any>(`projects/${this.projectId}/loan_requests/${loanRequest.id}`, {
            loan_request: loanRequest,
          })
          .pipe(
            map((response) => {
              let updatedLoanRequest = response.loan_request;

              // Find the index of the updated task
              const index = loanRequests.findIndex((item) => item.id === loanRequest.id);

              // Update the contact
              loanRequests[index] = updatedLoanRequest;

              // Update the contacts
              this._loanRequests.next(loanRequests);

              // Return the updated contact
              return updatedLoanRequest;
            }),
            switchMap((updatedLoanRequest) =>
              this.loanRequest$.pipe(
                take(1),
                filter((item) => item && item.id === updatedLoanRequest.id),
                tap(() => {
                  // Update the task if it's selected
                  this._loanRequest.next(updatedLoanRequest);

                  // Return the updated task
                  return updatedLoanRequest;
                }),
              ),
            ),
          ),
      ),
    );
  }

  deleteLoanRequest(id: number): Observable<any> {
    return this.loanRequests$.pipe(
      take(1),
      switchMap((loanRequests) =>
        this._httpClient.delete<any>(`projects/${this.projectId}/loan_requests/${id}`).pipe(
          map((reponse) => {
            // Find the index of the deleted label within the labels
            const index = loanRequests.findIndex((item) => item.id === id);

            // Delete the label
            loanRequests.splice(index, 1);

            // Update the labels
            this._loanRequests.next(loanRequests);

            // Return the deleted status
            return reponse.loan_request;
          }),
        ),
      ),
    );
  }

  createLoanQuote(loanRequestId: number, loanQuote: LoanQuote): Observable<LoanRequest> {
    return this.loanQuotes$.pipe(
      take(1),
      switchMap((loanQuotes) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/loan_requests/${loanRequestId}/loan_quotes`, { loan_quote: loanQuote })
          .pipe(
            map((reponse) => {
              // Update the contacts with the new contact
              this._loanQuotes.next([reponse.loan_quote, ...loanQuotes]);

              // Return the new contact
              return reponse.loan_quote;
            }),
          ),
      ),
    );
  }

  updateLoanRequote(loanRequestId: number, loanQuote: LoanQuote, skipUpdate = false): Observable<LoanQuote> {
    return this.loanQuotes$.pipe(
      take(1),
      switchMap((loanQuotes) =>
        this._httpClient
          .put<any>(`projects/${this.projectId}/loan_requests/${loanRequestId}/loan_quotes/${loanQuote.id}`, {
            loan_quote: loanQuote,
          })
          .pipe(
            map((response) => {
              let updatedLoanQuote = response.loan_quote;

              // Find the index of the updated task
              const index = loanQuotes.findIndex((item) => item.id === loanQuote.id);

              // Update the contact
              loanQuotes[index] = updatedLoanQuote;

              // Update the contacts
              if (!skipUpdate) {
                this._loanQuotes.next(loanQuotes);
              }

              // Return the updated contact
              return updatedLoanQuote;
            }),
          ),
      ),
    );
  }

  getLoanQuoteById(id: string): Observable<LoanQuote> {
    return this.loanRequest$.pipe(
      take(1),
      switchMap((loanRequest) =>
        this._httpClient.get<any>(`projects/${this.projectId}/loan_requests/${loanRequest.id}/loan_quotes/${id}`).pipe(
          map((response) => {
            this._loanQuote.next(response.loan_quote);
            return response.loan_quote;
          }),
        ),
      ),
    );
  }

  deleteLoanQuote(loanRequestId: number, id: number): Observable<any> {
    return this.loanQuotes$.pipe(
      take(1),
      switchMap((loanQuotes) =>
        this._httpClient
          .delete<any>(`projects/${this.projectId}/loan_requests/${loanRequestId}/loan_quotes/${id}`)
          .pipe(
            map((reponse) => {
              // Find the index of the deleted label within the labels
              const index = loanQuotes.findIndex((item) => item.id === id);

              // Delete the label
              loanQuotes.splice(index, 1);

              // Update the labels
              this._loanQuotes.next(loanQuotes);

              // Return the deleted status
              return reponse._loan_quote;
            }),
          ),
      ),
    );
  }

  getLoanQuoteStatuses(): Observable<TaskStatus[]> {
    return of(LOAN_QUOTE_STATES as TaskStatus[]);
  }
}

export const LOAN_QUOTE_STATES = [
  {
    key: 'sent',
    label: 'Sent',
    color: 'gray',
    icon: 'more_horiz',
  },
  {
    key: 'received',
    label: 'Received',
    color: 'blue',
    icon: 'more_horiz',
  },
  {
    key: 'rejected',
    label: 'Rejected',
    color: 'red',
    icon: 'error_outline',
  },
  {
    key: 'quoted',
    label: 'Quoted',
    color: 'yellow-300',
    icon: 'more_horiz',
  },
  {
    key: 'competitive',
    label: 'Competitive',
    color: 'teal-200',
    icon: 'more_horiz',
  },
  {
    key: 'accepted',
    label: 'Accepted',
    color: 'green',
    icon: 'done',
  },
];
